import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cln from 'classnames'
import { useTranslation } from 'react-i18next'

import { TARGETING_MODES } from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'

import allCountries from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/countries.json'
import { isObjectEmpty } from 'utils/helpers'

const Layout = ({ children, borderless = false }) => {
  const { t } = useTranslation()

  return (
    <div className="recommendation__details__running__section recommendation__details__running__section--targeting">
      <p className="recommendation__details__running__section__title">
        {t('Recommendations.campaign.published.geoTargeting')}
      </p>
      <p
        className={cln('recommendation__details__running__section__body', {
          borderless,
        })}
      >
        {children}
      </p>
    </div>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  borderless: PropTypes.bool,
}

export default function Targeting({ tc, tc_run_id, targeting }) {
  const { t } = useTranslation()

  const _targeting = useMemo(() => {
    return (
      targeting?.[0] ?? {
        targetingMode: TARGETING_MODES.fdTargeting,
        tc,
        tc_run_id,
      }
    )
  }, [targeting, tc, tc_run_id])

  if (_targeting.targetingMode === TARGETING_MODES.fdTargeting) {
    return (
      <Layout targetingMode={_targeting.targetingMode}>
        {t(`Recommendations.geoTargeting.options.${_targeting.targetingMode}`)}
      </Layout>
    )
  }

  if (_targeting.targetingMode === TARGETING_MODES.postalCodes) {
    const countries = _targeting.postalCodes.countries

    if (isObjectEmpty(countries)) {
      return (
        <Layout targetingMode={_targeting.targetingMode}>
          {t('Recommendations.campaign.published.noPostalCodesAdded')}
        </Layout>
      )
    }

    const mappedCountries = Object.keys(countries).map((countryKey) => {
      const country = allCountries[countryKey]

      return {
        name: country.name,
        key: countryKey,
        totalPostalCodes: countries[countryKey].length,
      }
    })

    return (
      <Layout borderless>
        {mappedCountries.map((country) => (
          <div key={country.key} className="postal-codes__country">
            <div className="postal-codes__country__container">
              {t('Recommendations.campaign.published.geoTargetingCountry', {
                country: country.name,
                countPostalCodes: country.totalPostalCodes,
              })}
            </div>
          </div>
        ))}
      </Layout>
    )
  }

  if (_targeting.targetingMode === TARGETING_MODES.rangeLocations) {
    const { included = [], excluded = [] } = _targeting.rangeLocations

    if ((!included || included.length === 0) && (!excluded || excluded.length === 0)) {
      return (
        <Layout targetingMode={_targeting.targetingMode}>
          {t('Recommendations.campaign.published.noRangeLocationsAdded')}
        </Layout>
      )
    }

    // Group locations by country/country group/region
    const groupKey = (loc) => {
      const l = loc.location
      // Always group by country if possible
      if (l.country_name) return l.country_name
      if (l.country_code && allCountries[l.country_code]) return allCountries[l.country_code].name
      if (l.is_worldwide) return l.name || 'Worldwide'
      if (l.type === 'country_group') return l.name
      if (l.region) return l.region
      return l.name || l.key
    }

    // Group and count types
    const allLocations = [...included, ...excluded]
    const grouped = {}
    allLocations.forEach((loc) => {
      const key = groupKey(loc)
      if (!grouped[key]) grouped[key] = {}
      const type = loc.location.type
      if (!grouped[key][type]) grouped[key][type] = 0
      grouped[key][type]++
    })

    // Render summary for each group
    return (
      <Layout borderless>
        {Object.entries(grouped).map(([group, typeCounts], idx) => {
          // Build summary string for this group
          const summaryParts = Object.entries(typeCounts).map(([type, count]) => {
            // Use translation keys for singular/plural
            const pluralKey = `Recommendations.geoTargeting.rangeLocations.locationTypes.${type}_plural`
            const singularKey = `Recommendations.geoTargeting.rangeLocations.locationTypes.${type}`
            // If plural translation exists and count > 1, use plural, else singular
            const label = (count > 1 && t(pluralKey, { defaultValue: null }))
              ? t(pluralKey)
              : t(singularKey)
            if ((type === 'country' || type === 'country_group')) {
              return label
            }
            return `${count} ${label}`
          })
          return (
            <div key={group + idx} className="range-locations__entry">
              <div className="range-locations__country">
                <div className="range-locations__country__container">
                  {group} – {summaryParts.join(', ')}
                </div>
              </div>
            </div>
          )
        })}
      </Layout>
    )
  }

  return null
}

Targeting.propTypes = {
  tc: PropTypes.string.isRequired,
  tc_run_id: PropTypes.number.isRequired,
  targeting: PropTypes.array,
}
